import React from "react";
import { MemberProfilePanel } from "#app/components/entity/member-profile-panel";
import { useAppData } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  useEntityDetail,
  type ActiveEntityDetail,
} from "#app/components/entity/entity-detail-provider";

/**
 * Whether the profile detail presents in the right-side island — true
 * whenever a user entity is active. Drives AppShell's `rightPanelOpen` AND
 * the island host's data fetching, so both stay in lockstep.
 */
export function useProfileIslandOpen() {
  const { active } = useEntityDetail();
  return active?.kind === "user";
}

/** Panel wiring: edit-profile routing, permission gating, and
 * profile-to-profile navigation. */
function useMemberProfileHostProps(userId: string | null) {
  const { openEntity, closeEntity } = useEntityDetail();
  const { currentUser } = useAppData();
  const { push } = useAppNavigate();
  const canManageUsers = usePermission("manage.users");

  const handleEditProfile = React.useCallback(
    (uid: string) => {
      // Edit routes to a settings page, but navigating doesn't clear
      // `active` — close the island first: the destination IS this profile's
      // edit UI, so a lingering panel just restates the page beside it.
      closeEntity();
      if (uid === currentUser?.userId) {
        push("/settings/profile");
      } else {
        push(`/settings/members/${uid}`);
      }
    },
    [closeEntity, push, currentUser?.userId],
  );

  const handleViewMember = React.useCallback(
    (uid: string) => openEntity("user", uid, { via: "profile" }),
    [openEntity],
  );

  const isSelf = userId === currentUser?.userId;

  return {
    onClose: closeEntity,
    isSelf,
    canManage: canManageUsers,
    currentUserId: currentUser?.userId ?? null,
    // Self-edit always allowed; editing anyone else is gated on manage.users.
    onEditProfile: canManageUsers || isSelf ? handleEditProfile : undefined,
    onViewMember: handleViewMember,
  };
}

/**
 * The user the island presents: the active user entity while one is active,
 * else the last one — so the slide-out keeps its content through the close.
 * Derived during render, not in an effect, so the island's first frame
 * already shows the member it was opened for instead of the previous one.
 */
function useHeldUserId(active: ActiveEntityDetail | null): string | null {
  const [held, setHeld] = React.useState<string | null>(null);
  const activeUserId = active?.kind === "user" ? active.id : null;
  if (activeUserId != null && activeUserId !== held) setHeld(activeUserId);
  return activeUserId ?? held;
}

/**
 * Connects {@link MemberProfilePanel} to the entity-detail context. Reads the
 * active entity, retains the last user id through the close transition (so
 * the island slides out with content intact), and owns edit-profile routing.
 * Mount this as AppShell's `rightPanel`; drive `rightPanelOpen` off
 * {@link useProfileIslandOpen}.
 */
export function MemberProfilePanelHost() {
  const { active } = useEntityDetail();
  const open = useProfileIslandOpen();
  const userId = useHeldUserId(active);
  const hostProps = useMemberProfileHostProps(userId);

  return <MemberProfilePanel userId={userId} open={open} {...hostProps} />;
}
